import { ReactNode, useEffect, useState } from "react";
import {useLocation} from "react-router-dom";
import {
  BiChevronsRight,
  BiChevronsLeft
} from "react-icons/bi";
import { BsFillHouseFill } from "react-icons/bs";
import { MdLanguage } from "react-icons/md";

import Logo from "../../../assets/images/logo_bg.png";
import { buildLogoutUrl } from "~/services/keycloak";
import { useTranslation } from "react-i18next";
import { languages } from "~/assets/locales/languages/Languages";
import i18n from "~/assets/locales/Lang";
import { vmTemplateSubItems } from "~/models/menu/VmTemplateSubMenu";
import { DeploymentScriptSubItems } from "~/models/menu/DeploymentScriptSubMenu";
import { getInitials } from "~/Utils/Utils";
import { SidebarNavLink } from "./SideBarNavLink";
import { SubMenu } from "./SubMenu";

interface MenuProps {
  children?: ReactNode;
}

export const SIMPLMenuHeader = ({ children }: MenuProps) => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isVmSubMenuOpen, setIsVmSubMenuOpen] = useState(false);
  const [isDeploymentSubMenuOpen, setIsDeploymentSubMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");

  const { t } = useTranslation();
  const location = useLocation();
  const preferredUsername = sessionStorage.getItem("preferred_username") || "";
  const familyName = sessionStorage.getItem("family_name") || "";
  // Language handling
  useEffect(() => {
    const savedLang = localStorage.getItem("lang") || "en";

    setSelectedLanguage(savedLang);

    if (i18n.language !== savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  // Auto-open submenu when any child route is active
  useEffect(() => {
    const isDeploymentChildActive = DeploymentScriptSubItems(t).some((item) => {
      const itemPath = item.to.replace(/\/$/, "");
      const currentPath = location.pathname.replace(/\/$/, "");

      return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
    });

    const isVmChildActive = vmTemplateSubItems(t).some((item) => {
      const itemPath = item.to.replace(/\/$/, "");
      const currentPath = location.pathname.replace(/\/$/, "");

      return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
    });

    setIsDeploymentSubMenuOpen(isDeploymentChildActive);
    setIsVmSubMenuOpen(isVmChildActive);
  }, [location.pathname, t]);

  const handleLanguageChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newLang = e.target.value;

    if (localStorage.getItem("lang") !== newLang) {
      localStorage.setItem("lang", newLang);
    }

    setSelectedLanguage(newLang);

    await i18n.changeLanguage(newLang);
  };

  const handleLogout = (): void => {
    const idToken = sessionStorage.getItem("id_token");
    const redirectUri = globalThis.location.origin;

    if (!idToken) {
      globalThis.location.href = redirectUri;
      return;
    }

    const logoutUrl = buildLogoutUrl(redirectUri, idToken);

    sessionStorage.clear();
    globalThis.location.href = logoutUrl;
  };

  return (
    <div className="flex flex-col h-screen relative bg-[#002B6E]">
      {/* Top Header */}
      <div className="flex justify-between items-center text-white px-2 sm:px-4 md:px-6 pl-0 h-[72px] relative z-20 bg-[#002B6E] border-b border-gray-700/50">
        {/* Logo */}
        <div className="flex items-center">
          <img src={Logo} alt="Logo" className="h-20 w-full mr-4" />
        </div>

        {/* Right Header Menu */}
        <div className="flex items-center gap-3 text-white pr-4">
          {/* User */}
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end leading-tight">
              <span className="text-[10px] text-white">Welcome</span>

              <span className="text-[12px] font-medium text-white">
                {preferredUsername } <span className="font-bold">{familyName }</span>
              </span>
            </div>

            {/* Avatar */}
          </div>
          <div className="flex items-center gap-3">
            <span className="w-10 h-9 rounded-full bg-[#4778D1] flex items-center justify-center text-[11px] font-semibold">
              {getInitials(preferredUsername, familyName)}
            </span>

            {/* User Dropdown */}
            <span className="w-4 h-4 rounded-full bg-[#51E7F9] flex items-center justify-center -ml-1">
              <select
                value=""
                onChange={handleLogout}
                className="w-5 h-4 bg-[#002B6E] text-white border-none focus:outline-none cursor-pointer p-0"
                aria-label={t("LogOut")}
              >
                <option value="" disabled hidden></option>
                <option value="logout">{t("LogOut")}</option>
              </select>
            </span>
          </div>

          {/* Language */}
          <div className="flex items-center gap-1">
            <MdLanguage size={20} className="text-white" />

            <select
              value={selectedLanguage}
              onChange={handleLanguageChange}
             className="bg-[#002B6E] text-white hover:text-white border-none focus:outline-none"
            >
              {languages.map((language) => (
                <option
                  key={language.value}
                  value={language.value}
                 className="bg-[#002B6E] text-white"
                >
                  {language.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 relative">
        {/* Sidebar */}
        <div
          className={`bg-[#002B6E] p-3 md:p-5 text-white transition-all duration-300 flex flex-col overflow-hidden relative z-10
            ${
              isSidebarExpanded
                ? "md:w-[320px]"
                : "md:w-[100px] md:rounded-tr-[20px] md:rounded-br-[20px]"
            }`}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between mb-8 mt-2">
            {isSidebarExpanded && (
              <span className="inline-flex items-center text-sm font-semibold">
                <BsFillHouseFill size={22} className="mr-1" />
                {t("Menu")}
              </span>
            )}

            {/* Collapse Button */}
            <button
              type="button"
              onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
              className={`${
                isSidebarExpanded ? "ml-auto" : "mx-auto"
              } w-6 h-6 rounded-md border-[1px] border-[#51E7F9]
              p-4 flex items-center justify-center transition-all duration-300`}
              aria-label={
                isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"
              }
            >
              {isSidebarExpanded ? (
                <span className="text-sm font-semibold text-[#51E7F9]">
                  <BiChevronsLeft size={14} />
                </span>
              ) : (
                <span className="text-sm font-semibold text-[#51E7F9]">
                  <BiChevronsRight size={14} />
                </span>
              )}
            </button>
          </div>

          {/* Navigation */}
          {isSidebarExpanded && (
            <nav className="flex-1">
              <ul className="space-y-3">
                <SubMenu
                  titleKey="DeploymentScript"
                  items={DeploymentScriptSubItems(t)}
                  isOpen={isDeploymentSubMenuOpen}
                toggleOpen={() => setIsDeploymentSubMenuOpen((prev) => !prev)}
              />

              <li className="mb-2 flex items-center">
                <SidebarNavLink
                  to="/contracts"
                  label={t("ProvisionedResources")}
                  isExpanded={isSidebarExpanded}
                  end={false}
                />
              </li>

              <li className="mb-2 flex items-center">
                <SidebarNavLink
                  to="/cloud-provisioner-template"
                  label={t("CloudProvisioner")}
                  isExpanded={isSidebarExpanded}
                />
              </li>

              <li className="mb-2 flex items-center">
                <SidebarNavLink
                  to="/cloud-environment"
                  label={t("CloudEnvironment")}
                  isExpanded={isSidebarExpanded}
                />
              </li>

              <SubMenu
                titleKey="VmTemplate"
                items={vmTemplateSubItems(t)}
                isOpen={isVmSubMenuOpen}
                toggleOpen={() => setIsVmSubMenuOpen((prev) => !prev)}
              />
            </ul>
          </nav>)}
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-6 flex-1 overflow-auto bg-white rounded-tl-[8px] shadow-inner">
          {children}
        </div>
      </div>
    </div>
  );
};
